import { horodatageLecture } from './conversations-lecture.js';
import { observerPastille, compteServeur, type Pastille } from './pastilles.js';
import { Timestamp } from 'firebase/firestore';
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  type Firestore,
} from 'firebase/firestore';
import { z } from 'zod';

/**
 * LES CONVERSATIONS AVEC LIA, UN DOCUMENT PAR SUJET, SOUS LE CHEMIN DE LEUR
 * AUTEUR : `orgs/{orgId}/liaConversations/{uid}/sujets/{sujetId}`.
 *
 * Pourquoi la, et pas dans une famille de porte : une conversation avec Lia
 * est PRIVEE a la personne qui la tient. Les rules d'aujourd'hui cadrent par
 * org et par porte (`docs/ACCES.md`), pas par participant : le seul moyen de
 * rendre un document prive sans le chantier « acces par fiche », c'est de le
 * ranger sous l'uid de son auteur et de comparer ce segment de chemin a
 * `request.auth.uid`. C'est ce que font les rules de ce chemin, et rien
 * d'autre : ni owner ni admin ne lisent la conversation d'un autre.
 *
 * Ce qui est conserve est BORNE : les derniers tours (`TOURS_MAX`), chacun
 * avec son texte tronque (`TEXTE_MAX`), les fiches citees pour les rouvrir, et
 * un simple compte des outils appeles. Les etapes d'outils completes, elles,
 * ne survivent pas a la session : elles sont un detail de la reponse, pas
 * l'historique.
 */
export const LIA_CONVERSATION_TOURS_MAX = 60;
export const LIA_CONVERSATION_TEXTE_MAX = 12_000;
export const LIA_CONVERSATION_TITRE_MAX = 80;
/** Les personnes (personId) a qui un sujet peut etre partage en LECTURE SEULE (retour 18 du 05/10/2026). Recopie dans les rules. */
export const LIA_CONVERSATION_PARTAGE_MAX = 20;

const ficheSuivieSchema = z.object({
  appId: z.string().min(1).max(64),
  doorId: z.string().min(1).max(64),
  recordId: z.string().min(1).max(128),
  titre: z.string().min(1).max(120),
});

const tourSchema = z.object({
  id: z.string().min(1).max(64),
  role: z.enum(['user', 'assistant']),
  text: z.string().max(LIA_CONVERSATION_TEXTE_MAX),
  at: z.number().int().nonnegative().optional(),
  records: z.array(z.object({
    id: z.string().min(1).max(128),
    title: z.string().max(200),
    appId: z.string().min(1).max(64),
    doorId: z.string().min(1).max(64),
    doorName: z.string().max(80).optional(),
    family: z.string().max(64).optional(),
  })).max(20).optional(),
  outils: z.object({
    lectures: z.number().int().nonnegative(),
    ecritures: z.number().int().nonnegative(),
    echecs: z.number().int().nonnegative(),
  }).optional(),
});

export const liaSujetInputSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
  titre: z.string().min(1).max(LIA_CONVERSATION_TITRE_MAX),
  conversationId: z.string().min(1).max(64),
  tours: z.array(tourSchema).max(LIA_CONVERSATION_TOURS_MAX),
  fiche: ficheSuivieSchema.nullable(),
  /** Les personIds qui LISENT ce sujet (jamais n'ecrivent). Absent : personne. */
  partageA: z.array(z.string().min(1).max(64)).max(LIA_CONVERSATION_PARTAGE_MAX).optional(),
});

export type LiaFicheSuivie = z.infer<typeof ficheSuivieSchema>;
export type LiaTour = z.infer<typeof tourSchema>;
export type LiaSujetInput = z.infer<typeof liaSujetInputSchema>;

export interface LiaSujet extends LiaSujetInput {
  creeLe: number;
  modifieLe: number;
  archiveLe: number | null;
}

/** Un sujet qu'une autre personne a partage a l'appelant : le meme, plus l'uid de son proprietaire. */
export interface LiaSujetPartage extends LiaSujet {
  /** L'uid du proprietaire (le segment du chemin) : l'appelant ne l'ecrit jamais. */
  proprietaireUid: string;
  orgId: string;
}

export interface LiaConversationsRepository {
  observerReponses(uid: string, sujetId: string, recevoir: (r: Array<{ id: string; texte: string; at: number; docId?: string }>) => void, erreur?: (e: unknown) => void): () => void;
  observerPastilles(uid: string, sujetId: string, lu: Date | undefined, recevoir: (p: Pastille) => void, erreur?: (e: unknown) => void, depuis?: number): () => void;
  /** Les sujets de la personne connectee, archives compris, du plus recent au plus ancien. */
  observer(onValue: (sujets: LiaSujet[]) => void, onError?: (error: unknown) => void): () => void;
  /** Cree ou reecrit un sujet entier (titre, tours bornes, fiche suivie). Les tours en trop sont coupes par le debut. */
  enregistrer(sujet: LiaSujetInput): Promise<void>;
  renommer(id: string, titre: string): Promise<void>;
  archiver(id: string): Promise<void>;
  desarchiver(id: string): Promise<void>;
  /** Alias de `desarchiver` : le vocabulaire commun (« Restaurer ») de toutes les conversations. */
  restaurer(id: string): Promise<void>;
  // ---- Le partage en lecture seule (contrat conversations du 05/10/2026, section 2d) ----
  /** Ajoute des personnes (personId) qui liront ce sujet. Proprietaire seul (rules). */
  partager(id: string, personIds: readonly string[]): Promise<void>;
  /** Retire des personnes du partage. Proprietaire seul (rules). */
  retirerPartage(id: string, personIds: readonly string[]): Promise<void>;
  /** Les sujets que d'autres m'ont partages, du plus recent au plus ancien. `maPersonne` : mon personId. */
  observerPartagesAvecMoi(onValue: (sujets: LiaSujetPartage[]) => void, onError?: (error: unknown) => void): () => void;
}

/** Les tours conserves : les `TOURS_MAX` derniers, chaque texte coupe a `TEXTE_MAX`. */
export function bornerToursLia(tours: readonly LiaTour[]): LiaTour[] {
  return tours.slice(-LIA_CONVERSATION_TOURS_MAX).map((tour) => tour.text.length > LIA_CONVERSATION_TEXTE_MAX
    ? { ...tour, text: tour.text.slice(0, LIA_CONVERSATION_TEXTE_MAX) } : tour);
}

function millis(value: unknown): number | null {
  if (value != null && typeof value === 'object' && 'toMillis' in value && typeof value.toMillis === 'function') {
    return value.toMillis() as number;
  }
  return null;
}

function sansUndefined<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Pure : un document de sujet brut en `LiaSujet`, ou `null` s'il est mal forme. */
export function sujetDe(id: string, data: Record<string, unknown>): LiaSujet | null {
  const parsed = liaSujetInputSchema.safeParse({
    id, titre: data['titre'], conversationId: data['conversationId'],
    tours: Array.isArray(data['tours']) ? data['tours'] : [], fiche: data['fiche'] ?? null,
    ...(Array.isArray(data['partageA']) ? { partageA: data['partageA'] } : {}),
  });
  if (!parsed.success) return null;
  const now = Date.now();
  return {
    ...parsed.data,
    creeLe: millis(data['createdAt']) ?? now,
    modifieLe: millis(data['updatedAt']) ?? now,
    archiveLe: millis(data['archivedAt']),
  };
}

/**
 * @param maPersonne - le personId de l'appelant (`acces-par-fiche.ts`), pour
 *   lire les sujets qu'on lui a partages ; absent dans les tests.
 */
export function createLiaConversationsRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  maPersonne?: () => Promise<string | null>,
): LiaConversationsRepository {
  const sujetsRef = () => collection(db, 'orgs', orgId, 'liaConversations', getActorId(), 'sujets');
  const sujetRef = (id: string) => doc(db, 'orgs', orgId, 'liaConversations', getActorId(), 'sujets', id);
  const verifierPersonnes = (personIds: readonly string[], geste: string): string[] => {
    const ids = [...new Set(personIds)];
    if (ids.length === 0 || ids.some((p) => typeof p !== 'string' || p.length === 0)) {
      throw new Error(`liaConversations.${geste} : au moins un personId non vide est attendu.`);
    }
    return ids;
  };
  return {
    async partager(id, personIds) {
      const ids = verifierPersonnes(personIds, 'partager');
      await updateDoc(sujetRef(id), { partageA: arrayUnion(...ids), updatedAt: serverTimestamp(), updatedBy: getActorId() });
    },
    async retirerPartage(id, personIds) {
      const ids = verifierPersonnes(personIds, 'retirerPartage');
      await updateDoc(sujetRef(id), { partageA: arrayRemove(...ids), updatedAt: serverTimestamp(), updatedBy: getActorId() });
    },
    observerPartagesAvecMoi(onValue, onError) {
      let arret: (() => void) | null = null;
      let ferme = false;
      const resoudre = maPersonne ?? (async () => null);
      resoudre().then((moi) => {
        if (ferme) return;
        if (moi === null) { onValue([]); return; }
        // Une requete de GROUPE : tous les `sujets` de l'org partages a moi.
        // Les rules la jugent par `orgId ==` et `partageA array-contains moi`.
        arret = onSnapshot(
          query(collectionGroup(db, 'sujets'), where('orgId', '==', orgId), where('partageA', 'array-contains', moi),
            orderBy('updatedAt', 'desc'), limit(100)),
          (snapshot) => onValue(snapshot.docs.flatMap((entry) => {
            const data = entry.data();
            const sujet = sujetDe(entry.id, data);
            const proprietaireUid = entry.ref.parent.parent?.id ?? '';
            return sujet === null || proprietaireUid === '' ? [] : [{ ...sujet, proprietaireUid, orgId }];
          })),
          onError,
        );
      }, (cause: unknown) => { if (onError) onError(cause); });
      return () => { ferme = true; if (arret !== null) arret(); };
    },
    observerReponses(uid, sujetId, recevoir, erreur) {
      return onSnapshot(query(collection(db, 'orgs', orgId, 'liaConversations', uid, 'sujets', sujetId, 'reponses'), orderBy('createdAt', 'desc'), limit(LIA_CONVERSATION_TOURS_MAX)), snap => recevoir(snap.docs.map(d => ({ id: d.get('requestId') as string, docId: d.id, texte: d.get('texte') as string, at: (d.get('createdAt')?.toMillis?.() ?? 0) as number })).reverse()), erreur);
    },
    observerPastilles(uid, sujetId, lu, recevoir, erreur, depuis) {
      const q = query(collection(db, 'orgs', orgId, 'liaConversations', uid, 'sujets', sujetId, 'reponses'));
      return observerPastille(q, () => compteServeur(query(q, ...(lu ? [where('createdAt', '>', horodatageLecture(lu))] : []))), recevoir, erreur, depuis);
    },
    observer(onValue, onError) {
      return onSnapshot(query(sujetsRef(), orderBy('updatedAt', 'desc')), (snapshot) => {
        onValue(snapshot.docs.flatMap((entry) => {
          const sujet = sujetDe(entry.id, entry.data());
          return sujet === null ? [] : [sujet];
        }));
      }, onError);
    },
    async enregistrer(sujet) {
      const uid = getActorId();
      const valide = liaSujetInputSchema.parse({ ...sujet, tours: bornerToursLia(sujet.tours) });
      const ref = sujetRef(valide.id);
      const corps = sansUndefined({
        titre: valide.titre, conversationId: valide.conversationId, tours: valide.tours, fiche: valide.fiche,
        ...(valide.partageA !== undefined ? { partageA: valide.partageA } : {}),
      });
      // Une transaction, pour que la premiere ecriture pose les champs de
      // naissance et que les suivantes ne les touchent plus : les rules les
      // gardent immuables, et un `set(merge)` aveugle les reecrirait.
      await runTransaction(db, async (transaction) => {
        const actuel = await transaction.get(ref);
        if (actuel.exists()) {
          transaction.update(ref, { ...corps, updatedAt: serverTimestamp(), updatedBy: uid });
        } else {
          transaction.set(ref, {
            ...corps, orgId, uid,
            createdAt: serverTimestamp(), createdBy: uid, updatedAt: serverTimestamp(), updatedBy: uid,
            archivedAt: null, schemaVersion: 1,
          });
        }
      });
    },
    async renommer(id, titre) {
      const propre = titre.trim().slice(0, LIA_CONVERSATION_TITRE_MAX);
      if (!propre) throw new Error('Un sujet garde un titre.');
      await updateDoc(sujetRef(id), { titre: propre, updatedAt: serverTimestamp(), updatedBy: getActorId() });
    },
    async archiver(id) {
      await updateDoc(sujetRef(id), { archivedAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: getActorId() });
    },
    async restaurer(id) {
      await updateDoc(sujetRef(id), { archivedAt: null, updatedAt: serverTimestamp(), updatedBy: getActorId() });
    },
    async desarchiver(id) {
      await updateDoc(sujetRef(id), { archivedAt: null, updatedAt: serverTimestamp(), updatedBy: getActorId() });
    },
  };
}
